import { useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Calendar } from "lucide-react"
import { cn } from "@/lib/utils"

interface DateRangePickerProps {
  startDate: string
  endDate: string
  onChange: (startDate: string, endDate: string) => void
}

const MAX_DAYS = 31

export const DateRangePicker = ({ startDate, endDate, onChange }: DateRangePickerProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState("")

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) return true
    const daysDiff = dayjs(end).diff(dayjs(start), "day") + 1
    return daysDiff <= MAX_DAYS
  }

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value
    setError("")
    
    if (!newStartDate) return
    
    if (dayjs(newStartDate).isAfter(dayjs(endDate))) {
      onChange(newStartDate, newStartDate)
      return
    }
    
    if (validateDateRange(newStartDate, endDate)) {
      onChange(newStartDate, endDate)
    } else {
      const maxEndDate = dayjs(newStartDate).add(MAX_DAYS - 1, "day").format("YYYY-MM-DD")
      const finalEndDate = dayjs(maxEndDate).isAfter(dayjs()) 
        ? dayjs().format("YYYY-MM-DD") 
        : maxEndDate
      onChange(newStartDate, finalEndDate)
      setError(t("analytics.maxDateRangeExceeded", { days: MAX_DAYS }))
    }
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value
    setError("")
    
    if (!newEndDate) return
    
    if (dayjs(newEndDate).isBefore(dayjs(startDate))) {
      onChange(newEndDate, newEndDate)
      return
    }
    
    if (validateDateRange(startDate, newEndDate)) {
      onChange(startDate, newEndDate)
    } else {
      const maxEndDate = dayjs(startDate).add(MAX_DAYS - 1, "day").format("YYYY-MM-DD")
      const finalEndDate = dayjs(maxEndDate).isAfter(dayjs()) 
        ? dayjs().format("YYYY-MM-DD") 
        : maxEndDate
      onChange(startDate, finalEndDate)
      setError(t("analytics.maxDateRangeExceeded", { days: MAX_DAYS }))
    }
  }

  const formatDisplayDate = (date: string) => {
    return dayjs(date).format("MMM D, YYYY")
  }

  const getDateRangeLabel = () => {
    if (startDate && endDate) {
      const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day")
      if (daysDiff === 0) {
        return formatDisplayDate(startDate)
      }
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
    }
    return t("analytics.selectDateRange")
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen)
          if (isOpen) setError("")
        }}
        className={cn(
          "flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-md",
          "text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500",
          "transition-colors min-w-[280px] justify-between"
        )}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-300" />
          <span className="text-sm">{getDateRangeLabel()}</span>
        </div>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false)
              setError("")
            }}
          />
          <div className="absolute top-full left-0 mt-2 z-20 bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-4 min-w-[320px]">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("analytics.startDate")}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  max={endDate || dayjs().format("YYYY-MM-DD")}
                  className={cn(
                    "w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md",
                    "text-white focus:outline-none focus:ring-2 focus:ring-blue-500",
                    "focus:border-transparent",
                    error && "border-red-500"
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t("analytics.endDate")}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  min={startDate}
                  max={dayjs(startDate).add(MAX_DAYS - 1, "day").isAfter(dayjs()) 
                    ? dayjs().format("YYYY-MM-DD")
                    : dayjs(startDate).add(MAX_DAYS - 1, "day").format("YYYY-MM-DD")}
                  className={cn(
                    "w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md",
                    "text-white focus:outline-none focus:ring-2 focus:ring-blue-500",
                    "focus:border-transparent",
                    error && "border-red-500"
                  )}
                />
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-900/20 border border-red-500/50 rounded-md p-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    const today = dayjs().format("YYYY-MM-DD")
                    const last30Days = dayjs().subtract(29, "day").format("YYYY-MM-DD")
                    onChange(last30Days, today)
                    setIsOpen(false)
                  }}
                  className={cn(
                    "flex-1 px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600",
                    "text-white rounded-md transition-colors"
                  )}
                >
                  {t("analytics.last30Days")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!error) {
                      setIsOpen(false)
                    }
                  }}
                  disabled={!!error}
                  className={cn(
                    "px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700",
                    "text-white rounded-md transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {t("analytics.apply")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

